import { Locale } from '@api-types/enums/locale.enum';
import { STORAGE_LOCALE_KEY } from '@core/services/locale.service';
import {
  ColorType,
  numberFormat,
  PatternObject,
  SeriesOptionsType,
} from 'highcharts';

export const NAVIGATOR_X_AXIS_ID = 'navigator-xAxis';
export const CHART_X_AXIS_ID = 'chart-xAxis';
export const CHART_Y_AXIS_ID = 'chart-yAxis';

function isPatternObject(opts: ColorType | undefined): opts is PatternObject {
  if (!opts) return false;
  if (Object.keys(opts).includes('pattern')) {
    return true;
  }

  return false;
}

export class BaseChartOptions {
  /**
   * Use this method when initializing a chart that does not have any data by default.
   * @returns Empty chart options
   */
  static getEmptyChartOptions = (): HighchartsOptions => ({
    lang: {
      locale: localStorage.getItem(STORAGE_LOCALE_KEY) ?? Locale.UK,
    },
    boost: {
      /* enabled: true,
      useGPUTranslations: true,
      seriesThreshold: 0,
      usePreallocated: true, */
    },
    series: [],
    rangeSelector: {},
    navigator: {
      maskFill: 'rgba(153, 217, 255, .2)', // TODO parametrize -- #99D9FF with less opacity
      xAxis: {
        id: NAVIGATOR_X_AXIS_ID,
      },
    },
    plotOptions: {
      series: {
        marker: {
          radius: 4,
          symbol: 'circle',
          states: {
            select: {
              fillColor: 'rgba(153, 217, 255, 1)', // TODO parametrize -- #99D9FF
              radius: 6,
            },
          },
        },
      },
    },
    xAxis: {
      title: {
        text: '',
      },
      min: null,
      max: null,
      type: 'datetime',
      id: CHART_X_AXIS_ID,
    },
    yAxis: {
      title: {
        text: '',
      },
      id: CHART_Y_AXIS_ID,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: true,
      reversed: false,
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      outside: true,
      style: {
        zIndex: 1000,
      },
      /* html */
      headerFormat: `
        <table class="font-body">
          <tr>
            <th colspan="2" class="text-sm text-start pb-2">{point.key}</th>
          </tr>
          <tr>
           <th colspan="2">
            <hr class="border-t-2 border-gray-200 rounded"/>
            </th>
          </tr>
            `,

      pointFormatter() {
        const isRangeType = this.series.type.includes('range');
        const color = isPatternObject(this.series.color)
          ? this.series.color.pattern.backgroundColor
          : this.series.color;
        /* html */
        return `<tr class="[&_td]:py-1">
            <td style="color: ${color}">
              ${this.series.name}
            </td>
            <td class="text-end pl-4">
              <b>
                ${
                  isRangeType
                    ? `${numberFormat(this.low ?? 0, 2)} - ${numberFormat(this.high ?? 0, 2)}`
                    : `${numberFormat(this.y ?? 0, 2)}`
                }
              </b>
            </td>
          </tr>`;
      },

      footerFormat: '</table>',
      shared: true,
    },
    colors: ['#193B8B', '#00A0F5', '#99D9FF', '#3AB44A', '#227942'],
    credits: {
      text: 'pacemaker.ai',
      href: 'https://pacemaker.ai/',
    },
    chart: {
      className:
        '[&_text]:!text-text [&_text]:!fill-text [&_text]:!font-body  [&_li]:!font-body',
    },
  });
}

export type HighchartsOptions = Omit<
  Highcharts.Options,
  'xAxis' | 'yAxis' | 'navigator'
> & {
  series: SeriesOptionsType[];
  xAxis: Highcharts.XAxisOptions & {
    title: {
      text: string;
    };
    min: number | null;
    max: number | null;
  };
  yAxis: Highcharts.YAxisOptions & {
    title: {
      text: string;
    };
  };
  navigator: NonNullable<Highcharts.NavigatorOptions>;
  rangeSelector: NonNullable<Highcharts.RangeSelectorOptions>;
};
